import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Plus, Trash2, FileText, ExternalLink } from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

const blankQuestion = () => ({
  type: "multiple_choice",
  text: "",
  options: ["", "", "", ""],
  correct_answer: "",
});

const blankTest = (title) => ({
  title: title || "Course assessment",
  description: "",
  passing_score: 70,
  time_limit_minutes: 30,
  status: "published",
  questions: [blankQuestion()],
});

function QuestionEditor({ q, index, onChange, onRemove }) {
  const setType = (type) =>
    onChange(
      type === "true_false"
        ? { ...q, type, options: ["True", "False"], correct_answer: "" }
        : { ...q, type, options: q.options?.length >= 2 ? q.options : ["", "", "", ""] },
    );

  return (
    <div
      data-testid={`question-${index}`}
      className="rounded-xl border border-slate-200 dark:border-white/10 p-4 space-y-3 bg-slate-50/60 dark:bg-white/[0.02]"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
          Question {index + 1}
        </span>
        <div className="flex items-center gap-2">
          <select
            className="rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1 text-[11px]"
            value={q.type}
            onChange={(e) => setType(e.target.value)}
            data-testid={`question-${index}-type`}
          >
            <option value="multiple_choice">Multiple choice</option>
            <option value="true_false">True / False</option>
          </select>
          <button type="button" onClick={onRemove} className={btnGhost} data-testid={`question-${index}-remove`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      <textarea
        rows={2}
        className={input}
        value={q.text}
        onChange={(e) => onChange({ ...q, text: e.target.value })}
        placeholder="Question text"
        data-testid={`question-${index}-text`}
      />

      <div className="space-y-2">
        {(q.type === "true_false" ? ["True", "False"] : q.options).map((opt, oi) => (
          <div key={oi} className="flex items-center gap-2">
            <input
              type="radio"
              name={`correct-${index}`}
              checked={q.correct_answer === opt && opt !== ""}
              onChange={() => onChange({ ...q, correct_answer: opt })}
              data-testid={`question-${index}-correct-${oi}`}
              className="h-3.5 w-3.5"
            />
            {q.type === "true_false" ? (
              <span className="text-sm">{opt}</span>
            ) : (
              <input
                className={input}
                value={opt}
                onChange={(e) => {
                  const options = [...q.options];
                  const prev = options[oi];
                  options[oi] = e.target.value;
                  onChange({
                    ...q,
                    options,
                    correct_answer: q.correct_answer === prev ? e.target.value : q.correct_answer,
                  });
                }}
                placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                data-testid={`question-${index}-option-${oi}`}
              />
            )}
          </div>
        ))}
        {q.type === "multiple_choice" && (
          <button
            type="button"
            onClick={() => onChange({ ...q, options: [...q.options, ""] })}
            className={btnGhost}
            data-testid={`question-${index}-add-option`}
          >
            <Plus className="h-3 w-3" /> Add option
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">Select the radio button next to the correct answer.</p>
    </div>
  );
}

export default function CertificationManager() {
  const [rows, setRows] = useState([]);
  const [slug, setSlug] = useState("");
  const [test, setTest] = useState(null);
  const [tpl, setTpl] = useState({ company_name: "", founder_name: "", signature_url: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: list }, { data: template }] = await Promise.all([
      req("get", "/admin/tests"),
      req("get", "/admin/certificate-template"),
    ]);
    setRows(list);
    setTpl(template);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCourse = async (row) => {
    setSlug(row.course_slug);
    if (row.has_test) {
      const { data } = await req("get", `/admin/tests/${row.course_slug}`);
      setTest({
        title: data.title,
        description: data.description || "",
        passing_score: data.passing_score,
        time_limit_minutes: data.time_limit_minutes || "",
        status: data.status,
        questions: data.questions || [],
      });
    } else {
      setTest(blankTest(`${row.course_title} Test`));
    }
  };

  const saveTest = async () => {
    setSaving(true);
    try {
      await req("put", `/admin/tests/${slug}`, {
        ...test,
        time_limit_minutes: test.time_limit_minutes === "" ? null : Number(test.time_limit_minutes),
        passing_score: Number(test.passing_score),
      });
      toast.success("Test saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save test");
    } finally {
      setSaving(false);
    }
  };

  const deleteTest = async () => {
    if (!window.confirm("Delete this test? Students will get certificates on 100% completion again.")) return;
    await req("delete", `/admin/tests/${slug}`);
    setSlug("");
    setTest(null);
    await load();
    toast.success("Test deleted");
  };

  const saveTemplate = async () => {
    try {
      await req("put", "/admin/certificate-template", tpl);
      toast.success("Certificate template saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save template");
    }
  };

  return (
    <div data-testid="admin-certification-manager" className="space-y-6">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden">
            <div className="px-4 py-3.5 border-b border-slate-100 dark:border-white/5">
              <h3 className="font-display text-base font-semibold tracking-tight">Course tests</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                A course with a published test issues its certificate only after the student passes.
              </p>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-white/5 max-h-[420px] overflow-y-auto">
              {rows.map((r) => (
                <button
                  key={r.course_slug}
                  type="button"
                  onClick={() => openCourse(r)}
                  data-testid={`test-course-${r.course_slug}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors ${
                    slug === r.course_slug ? "bg-slate-50 dark:bg-white/5" : ""
                  }`}
                >
                  <div className="text-sm font-medium truncate">{r.course_title}</div>
                  <div className="text-[11px] text-slate-400">
                    {r.has_test
                      ? `${r.question_count} questions · pass ${r.passing_score}% · ${r.status}`
                      : "No test yet"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3">
            <h3 className="font-display text-base font-semibold tracking-tight">Certificate template</h3>
            <input
              className={input}
              value={tpl.company_name || ""}
              onChange={(e) => setTpl({ ...tpl, company_name: e.target.value })}
              placeholder="Company / academy name"
              data-testid="tpl-company"
            />
            <input
              className={input}
              value={tpl.founder_name || ""}
              onChange={(e) => setTpl({ ...tpl, founder_name: e.target.value })}
              placeholder="Founder / instructor name"
              data-testid="tpl-founder"
            />
            <ImageUpload
              value={tpl.signature_url}
              onChange={(url) => setTpl({ ...tpl, signature_url: url })}
              folder="signature"
              label="Signature image (optional)"
              testid="tpl-signature-upload"
            />
            <div className="flex items-center gap-2">
              <button type="button" onClick={saveTemplate} className={btnDark} data-testid="tpl-save">
                <Check className="h-3 w-3" /> Save template
              </button>
              <a
                href={`${API}/admin/certificate-preview`}
                target="_blank"
                rel="noreferrer"
                className={btnGhost}
                data-testid="tpl-preview"
              >
                <ExternalLink className="h-3 w-3" /> Preview PDF
              </a>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
          {!test ? (
            <div className="h-full grid place-items-center py-16 text-center">
              <div>
                <FileText className="h-6 w-6 mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-400">
                  Pick a course on the left to build or edit its test.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-base font-semibold tracking-tight">Test settings</h3>
                <button type="button" onClick={deleteTest} className={btnGhost} data-testid="delete-test">
                  <Trash2 className="h-3 w-3" /> Delete test
                </button>
              </div>
              <input
                className={input}
                value={test.title}
                onChange={(e) => setTest({ ...test, title: e.target.value })}
                placeholder="Test title"
                data-testid="test-title"
              />
              <textarea
                rows={2}
                className={input}
                value={test.description}
                onChange={(e) => setTest({ ...test, description: e.target.value })}
                placeholder="Test description"
                data-testid="test-description"
              />
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                    Passing score %
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className={input}
                    value={test.passing_score}
                    onChange={(e) => setTest({ ...test, passing_score: e.target.value })}
                    data-testid="test-passing-score"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                    Time limit (min)
                  </span>
                  <input
                    type="number"
                    min={1}
                    className={input}
                    value={test.time_limit_minutes}
                    onChange={(e) => setTest({ ...test, time_limit_minutes: e.target.value })}
                    placeholder="optional"
                    data-testid="test-time-limit"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                    Status
                  </span>
                  <select
                    className={input}
                    value={test.status}
                    onChange={(e) => setTest({ ...test, status: e.target.value })}
                    data-testid="test-status"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
              </div>

              <div className="space-y-3">
                {test.questions.map((q, i) => (
                  <QuestionEditor
                    key={i}
                    q={q}
                    index={i}
                    onChange={(next) =>
                      setTest({ ...test, questions: test.questions.map((x, xi) => (xi === i ? next : x)) })
                    }
                    onRemove={() =>
                      setTest({ ...test, questions: test.questions.filter((_, xi) => xi !== i) })
                    }
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTest({ ...test, questions: [...test.questions, blankQuestion()] })}
                  className={btnGhost}
                  data-testid="add-question"
                >
                  <Plus className="h-3 w-3" /> Add question
                </button>
                <button type="button" onClick={saveTest} disabled={saving} className={btnDark} data-testid="save-test">
                  <Check className="h-3 w-3" /> {saving ? "Saving…" : "Save test"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
